import { SSE } from 'sse.js';
import { Observable, Subscriber} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable} from '@angular/core';
import { ParameterDto } from '../dto/justino/parameter.dto';
import { environment } from '../../environments/environment';
import { JustinoResponseDto } from '../dto/justino/justino-response.dto';
import { JustinoRequestDto } from '../dto/justino/justino-request.dto';
import { Constants } from '../constants/constants';
import { JustinoHistoryDto } from '../dto/justino/justino-history.dto';
import { ControlAccesoDto } from '../dto/justino/control-acceso.dto';

@Injectable({
  providedIn: 'root',
})
export class JustinoService {
  private eventSource!: EventSource;
  public headers = new HttpHeaders();
  private apiUrl = `${environment.justinoModelUrl}/api/`;
  private admUrl = `${environment.justinoApiUrl}/api/`;
  private parametersUrl = `${environment.justinoApiUrl}/api/parameters`;
  private justinoModelUrl = `${environment.justinoModelUrl}/api/justino-stream`;
  private justinoWebhookUrl = `${environment.justinoWebhookUrl}/api/justino-webhook`;

  constructor(
    private http: HttpClient
  ) {}

  getHeaders() {
    const authToken = sessionStorage.getItem(Constants.ACCESS_TOKEN) ?? '';
    return { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
    };
  }

  getParameterByName(parameterName: string): Observable<ParameterDto> {
    const url = `${this.parametersUrl}/name/${parameterName}`;
    return this.http.get<ParameterDto>(url, { headers: this.getHeaders() });
  }

  updateParameter(parameter: ParameterDto): Observable<ParameterDto> {
    const url = `${this.parametersUrl}/${parameter.Id}`;
    return this.http.put<ParameterDto>(url, parameter, { headers: this.getHeaders() });
  }

  getHistory(userID: string, sessionId: string): Observable<JustinoHistoryDto> {
    const url = `${environment.justinoModelUrl}/api/conversations`;
    return this.http.post<JustinoHistoryDto>(url,{
      user_id: userID,
      session_id: sessionId
    }, { headers: this.getHeaders() });
  }

  // Send a message to the Justino model
  sendMessage(dto: JustinoRequestDto): Observable<JustinoResponseDto> {
    let endPoint = this.justinoModelUrl;
    if (!dto.userId) {
      endPoint += '-public'; 
    }
    const eventSource = new SSE(endPoint, {
      headers: this.getHeaders(),
      payload: JSON.stringify(dto),
    });
    console.log('JustinoService.sendMessage: ', dto);
    return new Observable<JustinoResponseDto>(observer => {
      eventSource.addEventListener('chunk', function (e: any) {
        const response: JustinoResponseDto = JSON.parse(e.data);
        observer.next(response);
      });
      eventSource.addEventListener('stop', function (e: any) {
        const response: JustinoResponseDto = {
          conversationId: '',
          messageId: '',
          chunk: '',
          command: 'stop',
          status: 200,
          messageError: '',
        };
        observer.next(response);
      });
      eventSource.addEventListener('error', function (e: any) {
        const response: JustinoResponseDto = {
          conversationId: '',
          messageId: '',
          chunk: '',
          command: 'error',
          status: e.responseCode,
          messageError: 'Se ha producido un error, por favor intente de nuevo.',
        };
        observer.next(response);
      });
    });
  }

  getEventSource(session_id: string): EventSource {
    const url = `${this.justinoWebhookUrl}?sessionId=${encodeURIComponent(session_id)}`;
    console.log('JustinoService.suscribeWebhook: ', session_id);
    const options = { withCredentials: false };
    return new EventSource(url, options);
  }

  getWebhook(session_id: string): Observable<JustinoResponseDto> {
    let headers = new HttpHeaders();
    headers = this.headers.append('skip', 'true');
    const url = `${this.justinoWebhookUrl}/${session_id}`;
    return this.http.get<JustinoResponseDto>(url, { headers: headers });
  }

  suscribeWebhook(session_id: string): Observable<JustinoResponseDto> {
    return new Observable((subscriber: Subscriber<JustinoResponseDto>) => {
      const interval = setInterval(() => {
        this.getWebhook(session_id).subscribe((response: JustinoResponseDto) => {
          if (response.status === 200) {
            subscriber.next(response);            
          } else {
            subscriber.error(response);
          }
        });
      }, 500);
      return () => {
        clearInterval(interval);
        subscriber.complete();
      }
    });
  }

  download(conversationId: string, session_id: string): Observable<Blob> {
    return this.http.post(`${this.apiUrl}download-document/`,{
      conversation_id: conversationId,
      session_id: session_id
    } ,{
      responseType: 'blob',
      headers: this.getHeaders()
    });
  }

  deleteConversation(conversationId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}conversations/${conversationId}`, { headers: this.getHeaders() });
  }

  controlAcceso(idUsuario: string): Observable<any> {
    return this.http.post(`${this.admUrl}control-acceso/`, {
      IdUsuario: idUsuario
    }, { headers: this.getHeaders() });
  }

  getActivateToken(userID: string): Observable<any> {
    return this.http.post(`${this.apiUrl}get-activate`, {
      user_id: userID
    }, { headers: this.getHeaders() });
  }

  updateFeedback(idConversacion: string | undefined, idMensaje: string, feedback:string): Observable<any> {
    return this.http.post(`${this.admUrl}feedback/`, {
      idConversacion: idConversacion,
      idMensaje: idMensaje,
      feedback: feedback
    }, { headers: this.getHeaders() });
  }

  updateFunKB(): Observable<any> {
    return this.http.post(`${this.admUrl}update-fun-knowledge/`, {}, { headers: this.getHeaders() });
  }

  getPublicSession(): Observable<any> {
    return this.http.post(`${this.apiUrl}get-public-session/`, {});
  }

  updateLaKB(): Observable<any> {
    return this.http.post(`${this.admUrl}update-knowledge/`, {}, { headers: this.getHeaders() });
  }

  updateConversation(conversationId: string, name: string) {
    return this.http.put(
      `${this.apiUrl}conversations/${conversationId}`,
      { title: name },
      { headers: this.getHeaders() }
    );
  }

  close(): void {
    if (!this.eventSource) {
      return;
    }
    this.eventSource.close();
  }
}
